<template>
<div class='b-profile-change-password-page'>
    <div class='b-profile-change-password-page__head'>
        <div class='b-profile-change-password-page__title h-pl-10'>
            {{ $t('LABEL.PASSWORD') }}
        </div>
    </div>
    <div class='h-ph-20'>
        <form class='b-profile-change-password-page__form h-flex h-mt-30 h-mh--20'
              @keyup.enter='onSubmit'>
            <div class='h-flex-0-0-50p h-ph-10 h-pv-5'>
                <FwFormInput
                    class='h-mb-15'
                    :fieldHasError='validation.fieldHasErrorByRule(old_password, `old_password`, validation.passwordRequirements)'
                    labelType='label-thin'
                    :title='$t(`RESET.PASSWORD.CURRENT`)'>
                    <FwInput
                        :value='old_password'
                        type='password'
                        name='password'
                        autocomplete='current-password'
                        :placeholder='$t(`RESET.PASSWORD.EYE`)'
                        inputStyle='gray'
                        :error='validation.getFieldErrorTextByRule(old_password, `old_password`, validation.passwordRequirements)'
                        @update='value => old_password = value'
                        @input='validation.clearServerErrorsBase(`old_password`)'
                        @blur='validation.addCurrentInputToValidateArray(`old_password`)'>
                    </FwInput>
                </FwFormInput>
            </div>
            <div class='h-flex-0-0-50p h-ph-10 h-pv-5'>
                <FwFormInput
                    class='h-mb-15'
                    :fieldHasError='validation.fieldHasErrorByRule(new_password, `new_password`, validation.passwordRequirements)'
                    labelType='label-thin'
                    :title='$t(`RESET.PASSWORD.NEW`)'>
                    <FwInput
                        :value='new_password'
                        type='password'
                        name='password'
                        autocomplete='current-password'
                        :placeholder='$t(`RESET.PASSWORD.CHARACTERS`)'
                        inputStyle='gray'
                        :error='validation.getFieldErrorTextByRule(new_password, `new_password`, validation.passwordRequirements)'
                        @update='value => { new_password = value; validation.addCurrentInputToValidateArray(`user_confirm_password`); }'
                        @input='validation.clearServerErrorsBase(`new_password`)'
                        @blur='validation.addCurrentInputToValidateArray(`new_password`)'>
                    </FwInput>
                </FwFormInput>
            </div>
            <div class='h-flex-0-0-50p h-ph-10 h-pv-5'>
                <FwFormInput
                    class='h-mb-15'
                    :fieldHasError='!!confirmPasswordError'
                    labelType='label-thin'
                    :title='$t(`RESET.PASSWORD.NEW.CONFIRM`)'>
                    <FwInput
                        :value='user_confirm_password'
                        type='password'
                        name='password'
                        autocomplete='current-password'
                        :placeholder='$t(`RESET.PASSWORD.CHARACTERS`)'
                        inputStyle='gray'
                        :error='confirmPasswordError'
                        @update='value => user_confirm_password = value'
                        @input='validation.clearServerErrorsBase(`user_confirm_password`)'
                        @blur='validation.addCurrentInputToValidateArray(`user_confirm_password`)'>
                    </FwInput>
                </FwFormInput>
            </div>
        </form>
    </div>
    <div class='h-flex'>
        <FwButton
            class='h-mt-20 h-ml-10'
            :disabled='!isFormValid || !passwordMatch'
            borderRadiusType='tiny-border'
            @click='onSubmit'>
            {{ $t('BUTTON.CHANGE') }}
        </FwButton>
    </div>
</div>
</template>

<script lang='ts'>
import { Component, Emit, Prop, Vue } from 'vue-facing-decorator';
import { useValidation } from '@/composables/useValidation';
import { useGlobalStore } from '@/stores/useGlobalStore';

@Component
export default class ChangePasswordForm extends Vue {
    @Prop({ type: Boolean, default: false }) readonly disabledForm!: boolean;

    globalStore = useGlobalStore();
    avatarImage: string = 'https://www.stickees.com/files/avatars/male-avatars/1698-david-sticker.png';

    old_password: string = '';
    new_password: string = '';
    user_confirm_password: string = '';

    validation = useValidation();

    get passwordMatch() {
        return this.new_password === this.user_confirm_password;
    }

    get confirmPasswordError() {
        const passwordRequirementsError = this.validation.getFieldErrorTextByRule(
            this.user_confirm_password,
            'user_confirm_password',
            this.validation.passwordRequirements
        );
        if (passwordRequirementsError) {
            return passwordRequirementsError;
        }
        // @ts-ignore
        const isFieldTouched = this.validation.validationActiveArray.includes('user_confirm_password');
        if (isFieldTouched && this.user_confirm_password && this.new_password && !this.passwordMatch) {
            return this.$t('VALIDATION.FIELD.MATCHED');
        }
        return null;
    }

    get isFormValid() {
        return this.validation.passwordValidator({ value: this.new_password }) &&
            this.validation.passwordValidator({ value: this.old_password }) &&
            this.validation.passwordValidator({ value: this.user_confirm_password }) &&
            this.passwordMatch;
    }

    avatarUpload(img: string) {
        this.avatarImage = img;
    }

    backToEditPopup() {
        this.globalStore.showEditUserPopup(true);
        this.globalStore.showResetPasswordPopup(false);
    }

    @Emit('onSubmit')
    onSubmit() {
        return {
            old_password: this.old_password,
            new_password: this.new_password,
        };
    }
}
</script>

<style lang='sass'>
.b-profile-change-password-page
    max-width: 820px
    margin: 0 auto
    padding-top: 100px
    width: 100%
    color: #203F6A

    &__title
        font-size: 30px
        font-weight: bold
        letter-spacing: 0.25px
        line-height: 36px

    &__head
        display: flex
        justify-content: space-between
        width: 100%

    &__form
        display: flex
        flex-wrap: wrap
</style>
